import { MOMENT_FORMAT_DATE_SHORT, MOMENT_FORMAT_MONTH_YEAR_SHORT } from "util/formatters";
import extend from "lodash/extend";
import validator from "validator";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import isNaN from "lodash/isNaN";
import isNull from "lodash/isNull";
import isString from "lodash/isString";
import format from "string-format";
import includes from "lodash/includes";
import mapValues from "lodash/mapValues";
import moment from "moment";
import isEmptyGuid from "./isEmptyGuid";
import forEach from "lodash/forEach";
import split from "lodash/split";
import trim from "lodash/trim";
import endsWith from "lodash/endsWith";
import toLower from "lodash/toLower";
import formConverter from "./formConverters";
import isNil from "lodash/isNil";
import * as sentinel from "@sentinel/utils";

const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
const dateShortRegex = /^\d{2}\/\d{4}$/;
const dateTwoDigitShortRegex = /^\d{1,2}\/\d{4}$/;
const dateTwoDigitExtraShortRegex = /^\d{1,2}\/\d{2}$/;
const dateTimeRegex = /^\d{4}\-\d{2}\-\d{2}T\d{2}:\d{2}:\d{2}((\.\d{3}Z)|(\-|\+)\d{2}:\d{2})?$/;
const zipCodeRegex = /(^\d{5}$)|(^\d{5}\-\d{4}$)/;

const NumericalMinimum = 9e-7;

const { out: { convertPercentWithSign } } = formConverter;

const requiredHelper = (key, value, messageTemplate) => {
    if (isUndefined(value) || isNaN(value) || isNull(value) || isEmpty(value.toString())) {
        return format(messageTemplate, key);
    }
    return null;
};

const emailRegex = /^[a-zA-Z0-9_%'+-]+(?:\.[a-zA-Z0-9_%'+-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,63}$/;

const hasZeroWidthSpace = (email) => /[\u200B-\u200D\uFEFF]/.test(email);

const hasMixedScripts = (email) => {
    const hasLatin = /[A-Za-z]/.test(email);
    const hasCyrillic = /[\u0400-\u04FF]/.test(email);
    return hasLatin && hasCyrillic;
};

const isInvalidEmail = function(email) {
    if (!email || email.trim() === '') {
        return null;
    }

    if (hasZeroWidthSpace(email)) {
        return "Email contains invalid invisible characters.";
    }

    if (hasMixedScripts(email)) {
        return "Email cannot mix Latin and Cyrillic characters.";
    }

    if (!validator.matches(email, emailRegex)) {
        return "Email is not a valid email address.";
    }

    return null;
};

const _getMoment = (value) => {
    if (validator.matches(value, dateTwoDigitExtraShortRegex)) {
        return new moment(value, MOMENT_FORMAT_MONTH_YEAR_SHORT);
    }

    if (validator.matches(value, dateTwoDigitShortRegex)) {
        return new moment(value, MOMENT_FORMAT_DATE_SHORT);
    }

    if (validator.matches(value, dateRegex) || validator.matches(value, dateTimeRegex)) {
        return new moment(value);
    }

    return null;
};

const isNumberOutOfAcceptableRange = function(value) {
    if (value && value > Number.MAX_SAFE_INTEGER) {
        return "This number is too large";
    }
    // Numbers smaller than this are converted to exponential notation and validator does
    // not work with numbers in exponential notation. https://github.com/validatorjs/validator.js/issues/1309
    // Consider updating once that issue is resolved. For now, product confirmed that numbers of
    // this size should not be accepted anywhere in the site.
    if (value && value > 0 && value <= NumericalMinimum) {
        return "This number is too small";
    }
    if (value < 0) {
        return "Number cannot be negative";
    }
    return null;
};

// All of our supported validations, with pretty messages (or at least support to pass in pretty messages).
export let defs = {
    isMemberOf(values, message, value) {
        if (!includes(values, value)) {
            return message;
        }
        return null;
    },

    isNotMemberOf(values, message, value) {
        if (includes(values, value)) {
            return message;
        }
        return null;
    },

    isRequired(key, value) {
        return requiredHelper(key, value, "{0} is required");
    },

    areRequired(message = "{0} are required", key, value) {
        return requiredHelper(key, value, message);
    },

    isNonEmptyGuid(key, value) {
        if (isEmptyGuid(value.toString())) {
            return format("{0} is required", key);
        }
        return null;
    },

    isGuid(value) {
        if (!isEmpty(value) && !validator.isUUID(value)) {
            return "This isn't a valid GUID";
        }
        return null;
    },

    isEmail(value) {
        const err = isInvalidEmail(value);
        if (err) {
            return err;
        }
        return null;
    },

    isDomainName(value) {
        if (!isEmpty(value) && !validator.isFQDN(value)) {
            return "This isn't a valid domain name";
        }
        return null;
    },

    // This is part of our strategy to prevent ourselves from adding non-gradifi emails to our test lists in Responsys
    isEmailInternalDomain(value) {
        if (endsWith(toLower(value), "@gradifi.com") || endsWith(toLower(value), "@vestwell.com")) {
            return null;
        }
        return "Email address must end in either '@gradifi.com' or '@vestwell.com'";
    },

    isEmailList(value) {
        if (isEmpty(value)) {
            return null;
        }
        const segments = split(value, ",").map(s => trim(s)).filter(s => s.length > 0);
        if (segments.length === 0) {
            return " You have entered 1 invalid email address. ";
        }
        let invalidEmailCount = 0;
        forEach(segments, email => {
            if (isInvalidEmail(email)) {
                invalidEmailCount++;
            }
        });
        if (invalidEmailCount > 0) {
            return ` You have entered ${invalidEmailCount} invalid email ${invalidEmailCount > 1 ? "addresses" : "address"}. `;
        }
        return null;
    },

    isDate(value) {
        if (isNil(value)) {
            return null;
        }

        const invalidDateMessage = "This isn't a valid date. Use format MM/DD/YYYY";
        const dateRegexMatch = validator.matches(value, dateRegex);
        if (!isEmpty(value) && !dateRegexMatch && !validator.matches(value, dateTimeRegex)) {
            return invalidDateMessage;
        }

        if (dateRegexMatch) {
            let date = moment(value);
            if (!date.isValid()) {
                return invalidDateMessage;
            }
        }

        return null;
    },

    isValidShortDate(value) {
        if (!isEmpty(value) && !validator.matches(value, dateShortRegex)) {
            return "This isn't a valid date. Use format MM/YYYY";
        }
        return null;
    },

    isValidShortOrExtraShortDate(value) {
        let date = "Invalid date";
        if (validator.matches(value, dateTwoDigitExtraShortRegex)) {
            date = moment(value, MOMENT_FORMAT_MONTH_YEAR_SHORT).format(MOMENT_FORMAT_DATE_SHORT).toString();
        } else if (validator.matches(value, dateTwoDigitShortRegex)) {
            date = moment(value, MOMENT_FORMAT_DATE_SHORT).format(MOMENT_FORMAT_DATE_SHORT).toString();
        }

        return (date === "Invalid date") ? "This isn't a valid date." : null;
    },

    // The "now" parameter exists to support TimeWarp. Callers need to know what "now" is based on session data.
    isAgeOrOlderAsOf(now, age, value) {
        if (moment(now).diff(value, "years") < age) {
            return format("Must be {0} years old or older.", age);
        }
        return null;
    },

    // The "now" parameter exists to support TimeWarp. Callers need to know what "now" is based on session data.
    isAgeOrYoungerAsOf(now, age, value) {
        if (moment(now).diff(value, "years") > age) {
            return format("Must be {0} years old or younger.", age);
        }
        return null;
    },

    // The "now" parameter exists to support TimeWarp. Callers need to know what "now" is based on session data.
    isDatePast(now, value) {
        if (!isEmpty(value) && !includes(moment(value).from(now), "ago")) {
            return "This date is not in the past";
        }
        if (!isEmpty(value) && moment(value).isBefore(new Date(1900, 0, 1))) {
            return "Date must be after or equal to January 1st, 1900";
        }
        return null;
    },

    isDateFuture(message, now, value) {
        if (!isEmpty(value) && !_getMoment(value).isAfter(now)) {
            return message || "This date is not in the future";
        }
        return null;
    },

    isDateWithinRange(minDate, maxDate, value) {
        let date = Date.parse(value);
        if (date >= minDate && date <= maxDate) {
            return null;
        }
        return `Date must be within range: ${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
    },

    isDateWithinGradifiHistoryRange(value) {
        const minDate = new Date("1/1/2016");
        const maxDate = new Date("12/31/9999");
        let date = Date.parse(value);
        if (date >= minDate && date <= maxDate) {
            return null;
        }
        return `Date must be within range: ${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
    },

    isPhoneNumber(value) {
        // validator.isMobilePhone does not cover all 10 digit combos
        if (!isEmpty(value) && (value.match(/\d/g) || []).length !== 10) {
            return "This isn't a valid 10-digit phone number";
        }
        return null;
    },

    isNumeric(value) {
        const outOfRangeMessage = isNumberOutOfAcceptableRange(value);
        if (!isNull(outOfRangeMessage)) {
            return outOfRangeMessage;
        }

        if (value && !validator.isNumeric(value)) {
            return "This must be a valid number";
        }
        return null;
    },

    isDecimal(value) {
        const outOfRangeMessage = isNumberOutOfAcceptableRange(value);
        if (!isNull(outOfRangeMessage)) {
            return outOfRangeMessage;
        }

        if (value && !validator.isDecimal(value)) {
            return "This must be a valid number";
        }
        return null;
    },

    isDecimalIgnorePercentSign(value) {
        const cleanValue = convertPercentWithSign(value);
        const outOfRangeMessage = isNumberOutOfAcceptableRange(cleanValue);
        if (!isNull(outOfRangeMessage)) {
            return outOfRangeMessage;
        }

        if (cleanValue && !validator.isDecimal(cleanValue)) {
            return "This must be a valid number";
        }
        return null;
    },

    isInteger(value) {
        const outOfRangeMessage = isNumberOutOfAcceptableRange(value);
        if (!isNull(outOfRangeMessage)) {
            return outOfRangeMessage;
        }

        if (value && !validator.isInt(value)) {
            return "This must be a valid integer";
        }
        return null;
    },

    isGreaterThan(message, min, value) {
        if (value && (!validator.isFloat(value) || value <= min)) {
            return message;
        }
        return null;
    },

    isGreaterThanOrEqual(message, min, value) {
        if (value && (!validator.isFloat(value) || value < min)) {
            return message;
        }
        return null;
    },

    isGreaterThanOrEqualWithPercentSign(message, min, value) {
        const cleanValue = convertPercentWithSign(value);

        if (cleanValue && (!validator.isFloat(cleanValue) || cleanValue < min)) {
            return message;
        }
        return null;
    },

    isLessThan(key, max, value) {
        if (value && (!validator.isFloat(value) || value >= max)) {
            return format("{0} must be less than {1}", key, max);
        }
        return null;
    },

    isLessThanWithPercentSign(message, max, value) {
        const cleanValue = convertPercentWithSign(value);

        if (cleanValue && (!validator.isFloat(cleanValue) || cleanValue >= max)) {
            return message;
        }
        return null;
    },

    isLessThanOrEqual(message, max, value) {
        if (value && (!validator.isFloat(value) || value > max)) {
            return message;
        }
        return null;
    },

    isAlphanumeric(value) {
        if (!isNil(value) && !/^[a-zA-Z0-9_\-]*$/.test(value)) {
            return format("{0} is not alphanumeric", value);
        }
        return null;
    },

    hasTwoOrLessDecimalPlaces(value) {
        if (!isNil(value) && !validator.matches(value, /^\d+(\.\d{1,2})?$/)) {
            return format("{0} has more than 2 decimal places", value);
        }
        return null;
    },

    isChecked(message, value) {
        if (isString(value)) {
            return value !== "true" ? message : null;
        } else if (!value) {
            return message;
        }
        return null;
    },

    isTin(value) {
        if (!isNil(value) && !validator.matches(value, /^(\d{2}-\d{7})$/)) {
            return "TIN or FEIN must be in the following format: ##-#######";
        }
        return null;
    },

    isTaxId(value) {
        if (!isNil(value) && !validator.matches(value, /^(\d{3}-\d{2}-\d{4})$/)) {
            return "SSN or ITIN must be in the following format: XXX-XX-XXXX";
        }
        return null;
    },

    isRoutingNumber(value) {
        if (!isNil(value) && !validator.matches(value, /^\d{9}$/)) {
            return "Routing number must be in the following format: #########";
        }
        return null;
    },

    isServicerCode(value) {
        if (!isNil(value) && !validator.matches(value, /^[A-Z]{4}\d{3}$/)) {
            return "Servicer code must be in the following format: AAAA###";
        }
        return null;
    },

    isErpCode(value) {
        if (!isNil(value) && !validator.matches(value, /^[A-Z]{3}\d{3}$/)) {
            return "ERP code must be in the following format: AAA###";
        }
        return null;
    },

    isLengthLessThanOrEqual(key, upperLength, value) {
        if (!isNil(value) && !validator.isLength(value, 0, upperLength)) {
            return format("{0} must be no more than {1} characters", key, upperLength);
        }
        return null;
    },

    isZipCode(value) {
        if (!isEmpty(value) && !validator.matches(value, zipCodeRegex)) {
            return "This isn't a valid U.S. zip code";
        }
        return null;
    },

    isLength(key, length, value) {
        if (!isEmpty(value) && !validator.isLength(value, length)) {
            return format("{0} must be at least {1} characters", key, length);
        }
        return null;
    },

    /*
     * Password validation function
     * Long and separated to be able to provide proper feedback
     */
    isPassword(form, value) {
        if (isNil(value)) {
            return null;
        }

        const firstName = form?.data?.get("firstName");
        const lastName = form?.data?.get("lastName");
        const email = form?.data?.get("email");
        
        const contextWords = [
            email,
            email?.toLowerCase(),
            firstName,
            firstName?.toLowerCase(),
            lastName,
            lastName?.toLowerCase()
        ].filter(Boolean);

        const schema = sentinel.validatePassword(contextWords);

        try {
            schema.validateSync(value, { abortEarly: false });
            return null;
        } catch (err) {
            const messages = (err.inner || []).map(e => e.message);
            return messages.length ? messages : (err.errors || []).slice();
        }
    },

    isLessThanOrEqualToDataValue(key, message, value, data) {
        if (value <= data.get(key)) {
            return null;
        }
        return message;
    },

    isLessThanDataValue(key, message, value, data) {
        if (value < data.get(key)) {
            return null;
        }
        return message;
    },

    isGreaterThanOrEqualToDataValue(key, message, value, data) {
        if (value >= data.get(key)) {
            return null;
        }
        return message;
    },

    isGreaterThanDataValue(key, message, value, data) {
        if (value > data.get(key)) {
            return null;
        }
        return message;
    },

    equalsDataValue(key, message, value, data) {
        if (data.get(key) !== value) {
            return message;
        }
        return null;
    },

    equalsDataValueCaseIgnored(key, message, value, data) {
        if (toLower(data.get(key)) !== toLower(value)) {
            return message;
        }
        return null;
    },

    notEqualsDataValue(key, message, value, data) {
        if (data.get(key) === value) {
            return message;
        }
        return null;
    },

    is(messageFunction, value, data, fieldName, parentData, form) {
        return messageFunction(value, fieldName, form);
    },

    isLowerCase(key, value) {
        if (!isEmpty(value) && value !== toLower(value)) {
            return format("{0} must be lowercase", key);
        }
        return null;
    },

    hasNoSpaces(key, value) {
        if (isNil(value)) {
            return null;
        }

        if (validator.matches(value, /\s/)) {
            return format("{0} must not contain spaces", key);
        }
        return null;
    },

    matchesRegExp(customRegExp, errorMessage, value) {
        if (!validator.matches(value, new RegExp(customRegExp))) {
            return errorMessage;
        }
        return null;
    }
};

// Wrap all of the validations so that they are chainable.
let wrapFunction = function(validationFn, stopFn) {
    return function() {
        let context = extend({ __validationContext: true, fn: validationFn, stop: stopFn, args: arguments }, defs);
        if (this.__validationContext) {
            this.next = context;
            context.root = this.root;
        } else {
            context.root = context;
        }
        return context;
    };
};

defs = mapValues(defs, v => wrapFunction(v));

// Use this to short-circuit a validation.
defs.conditional = wrapFunction(undefined, function(fn, value, data, fieldName, parentData, form) {
    return !fn(value, fieldName, form);
});

// And add the validate() method that can be called at the end of the chain.
// Returns errors if there are any, and otherwise is undefined.
defs.validate = function(fieldName, form) {
    const rawValue = form.data.get(fieldName);
    const value = (isUndefined(rawValue) || isNull(rawValue) || isNaN(rawValue)) ? rawValue : rawValue.toString();
    const parentData = isNull(form.parent) ? {} : form.parent;
    const data = form.data;

    if (!this.__validationContext) {
        throw new Error("Invalid validation chain");
    }
    let errors = [];
    let next = this.root;
    while (next) {
        let args = [].concat(Array.prototype.slice.apply(next.args)).concat([value, data, fieldName, parentData, form]);
        // Conditionally stop validations.
        if (next.stop) {
            if (next.stop(...args)) {
                return errors.length ? errors : undefined;
            }
        } else {
            let error = next.fn(...args);
            if (error) {
                return [error];
            }
        }
        next = next.next;
    }

    if (errors.length > 0) {
        return errors;
    }
    return undefined;
};

export default defs;
